import React, { useEffect, useMemo, useState } from 'react';
import { useMainMedicalExpensePrices } from '@/hooks/main/useMainMedicalExpensePrice';
import {
  AppTextField,
  AppPagination,
  AppTypography,
  AppCard,
  AppSpace,
  AppRow,
  AppCol,
  AppSpin,
} from '@/components/atoms';
import { CollapsibleCard, InfoRow } from '@/components/molecules';
import { convGtJDate } from '@/utils/dateConverter';
import { useTheme } from '@/shared/providers/theme';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type { MedicalExpensePrice } from './types';

const LAW_REVISION_DATE = '2024-10-01';
const LAW_REVISION_DATE_JUN_2024_START = '2024-06-01';
const LAW_REVISION_DATE_JUN_2024_END = '2024-09-30';
const SENTINEL_END_DATE = '2100-12-31';

const formatAvailableDate = (value: string | null | undefined): string => {
  if (!value || value === SENTINEL_END_DATE) return '';
  return convGtJDate(value);
};

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '―';
  return `${value.toLocaleString('ja-JP')} 円`;
};

const isAfterOrEqualLawRevision = (availableFrom: string | null | undefined): boolean => {
  if (!availableFrom) return false;
  return availableFrom >= LAW_REVISION_DATE;
};

const isLawRevisionJunToSep2024 = (availableFrom: string | null | undefined): boolean => {
  if (!availableFrom) return false;
  return (
    availableFrom >= LAW_REVISION_DATE_JUN_2024_START &&
    availableFrom <= LAW_REVISION_DATE_JUN_2024_END
  );
};

interface A2NewLawTreatmentRow {
  label: string;
  value: number | null | undefined;
}

const A2NewLawTreatmentBlock: React.FC<{
  typeNumber: 1 | 2;
  record: MedicalExpensePrice | null;
}> = ({ typeNumber, record }) => {
  const rows: A2NewLawTreatmentRow[] = [
    { label: '初検料', value: record?.initial_inspection_fee },
    { label: '施術料', value: record?.treatment_price },
    { label: '訪問施術料1', value: record?.visit_treatment_price1 },
    { label: '訪問施術料2', value: record?.visit_treatment_price2 },
    { label: '訪問施術料3（3人～9人の場合）', value: record?.visit_treatment_price3 },
    { label: '訪問施術料3（10人以上の場合）', value: record?.visit_treatment_price4 },
  ];
  return (
    <AppCard className="p-2">
      {rows.map(({ label, value }) => (
        <div key={label} className="flex items-center py-1">
          <div className="w-1/2 font-bold text-gray-800 min-h-[32px] flex items-center">
            {label}
          </div>
          <div className="w-20 text-gray-800 min-h-[32px] flex items-center justify-center">
            （{typeNumber}術）
          </div>
          <div className="flex-1 pb-1 border-b border-gray-300 min-h-[32px] flex items-center justify-end">
            <span>{formatNumber(value)}</span>
          </div>
        </div>
      ))}
    </AppCard>
  );
};

// A2 has a small bounded dataset (~20 rows) — fetch all on the frontend so we
// can paginate per-period for new-law (showing both treatment_types together)
// while keeping per-record pagination for old-law (preserving existing UX).
const A2_FETCH_ALL_PER_PAGE = 200;

interface A2Page {
  records: MedicalExpensePrice[];
  isNewLaw: boolean;
}

const MedicalExpensePricePage: React.FC = () => {
  const [page, setPage] = useState(1);
  const { serviceType } = useTheme();
  const isA2 = serviceType === SERVICE_TYPE_A2;
  // A2: fetch all once and paginate locally. R2: paginate normally (1 record per page).
  const fetchPerPage = isA2 ? A2_FETCH_ALL_PER_PAGE : 1;
  const fetchPage = isA2 ? 1 : page;

  useEffect(() => {
    setPage(1);
  }, [serviceType]);

  const { data, isError, isLoading, isFetching } = useMainMedicalExpensePrices(
    fetchPerPage,
    fetchPage
  );

  const items = data?.data.data ?? [];

  // A2: build hybrid pagination units — per-period for new-law, per-record for old-law.
  const a2Pages = useMemo<A2Page[]>(() => {
    if (!isA2) return [];
    const groups = new Map<string, MedicalExpensePrice[]>();
    items.forEach(item => {
      const key = item.available_from ?? '';
      const bucket = groups.get(key) ?? [];
      bucket.push(item);
      groups.set(key, bucket);
    });
    // available_from desc to match backend ordering (latest period first).
    const orderedKeys = Array.from(groups.keys()).sort((a, b) => b.localeCompare(a));
    const result: A2Page[] = [];
    orderedKeys.forEach(key => {
      const records = groups.get(key) ?? [];
      const isNewLaw = isAfterOrEqualLawRevision(key);
      if (isNewLaw) {
        result.push({ records, isNewLaw: true });
      } else {
        // Old-law: one page per record (treatment_type), id desc.
        records
          .slice()
          .sort((a, b) => b.id - a.id)
          .forEach(record => result.push({ records: [record], isNewLaw: false }));
      }
    });
    return result;
  }, [isA2, items]);

  const a2CurrentPage = isA2 ? (a2Pages[page - 1] ?? null) : null;

  const a2Record1 = useMemo<MedicalExpensePrice | null>(
    () => a2CurrentPage?.records.find(r => r.treatment_type === 1) ?? null,
    [a2CurrentPage]
  );
  const a2Record2 = useMemo<MedicalExpensePrice | null>(
    () => a2CurrentPage?.records.find(r => r.treatment_type === 2) ?? null,
    [a2CurrentPage]
  );

  const current = useMemo(() => {
    if (isA2) return a2CurrentPage?.records[0] ?? null;
    return items[0] ?? null;
  }, [isA2, a2CurrentPage, items]);
  const display = current ?? ({} as Record<string, null>);

  if (isError) {
    return (
      <div className="px-4 py-3 text-red-700 border border-red-200 rounded bg-red-50">
        <AppTypography.P>データの取得に失敗しました。</AppTypography.P>
      </div>
    );
  }

  // For A2 the law status is determined by the current page (already grouped);
  // for R2, by the displayed record's available_from.
  const showNewLaw = isA2
    ? (a2CurrentPage?.isNewLaw ?? false)
    : isAfterOrEqualLawRevision(display.available_from);

  const showNewLawJun2024 = isLawRevisionJunToSep2024(display.available_from);

  // A2: treatment price based on type
  const treatmentPriceAcuAll =
    display.treatment_type === 2 ? formatNumber(display.treatment_price) : '―';
  const treatmentPriceAcuOnly =
    display.treatment_type === 1 ? formatNumber(display.treatment_price) : '―';

  if (isLoading) {
    return (
      <div
        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md bg-white/80"
        aria-busy="true"
      >
        <AppSpin size="large" />
      </div>
    );
  }

  // --- Shared: Distance prices (old law right column for both A2 and R2) ---
  const distancePricesBlock = showNewLawJun2024 ? (
    <>
      <InfoRow
        label="4km以下"
        value={formatNumber(display.distance_price4)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="4.1km～16km"
        value={formatNumber(display.distance_price16)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
    </>
  ) : (
    <>
      <InfoRow
        label="2km以下"
        value={formatNumber(display.distance_price2)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="2.1km～4km"
        value={formatNumber(display.distance_price4)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="4.1km～6km"
        value={formatNumber(display.distance_price6)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="6.1km～8km"
        value={formatNumber(display.distance_price8)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="8.1km～10km"
        value={formatNumber(display.distance_price10)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="10.1km～12km"
        value={formatNumber(display.distance_price12)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="12.1km～14km"
        value={formatNumber(display.distance_price14)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="14.1km～16km"
        value={formatNumber(display.distance_price16)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="往療距離加算2㎞毎"
        value={formatNumber(display.distance_add_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
    </>
  );

  // --- A2 new-law shared (right column) source: prefer record1, fallback record2 ---
  const a2SharedSource = a2Record1 ?? a2Record2 ?? (display as MedicalExpensePrice);

  // --- A2 New Law: stacked 1術 + 2術 blocks (left column) ---
  const a2NewLawLeftColumn = (
    <AppSpace orientation="vertical" size="middle" className="w-full">
      <A2NewLawTreatmentBlock typeNumber={1} record={a2Record1} />
      <A2NewLawTreatmentBlock typeNumber={2} record={a2Record2} />
    </AppSpace>
  );

  // --- A2 New Law: shared fees (right column) ---
  const a2NewLawRightColumn = (
    <AppCard className="p-2">
      <InfoRow
        label="電療料"
        value={formatNumber(a2SharedSource.electric_treatment_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="特別地域加算"
        value={formatNumber(a2SharedSource.special_area_addition_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      {showNewLawJun2024 ? (
        <>
          <InfoRow
            label="4km以下"
            value={formatNumber(a2SharedSource.distance_price4)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
          <InfoRow
            label="4.1km～16km"
            value={formatNumber(a2SharedSource.distance_price16)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
        </>
      ) : (
        <InfoRow
          label="往療料"
          value={formatNumber(a2SharedSource.distance_price2)}
          labelWidth="w-1/2"
          classNameLabel="text-gray-800"
          classNameValue="text-right"
        />
      )}
      <InfoRow
        label="施術報告書交付料"
        value={formatNumber(a2SharedSource.treatment_report_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
    </AppCard>
  );

  // --- A2 Old Law Left Column ---
  const a2OldLawLeftColumn = (
    <AppCard className="p-2">
      <InfoRow
        label="初検料"
        value={formatNumber(display.initial_inspection_fee)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="はり・きゅう"
        value={treatmentPriceAcuAll}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="はり"
        value={treatmentPriceAcuOnly}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="きゅう"
        value={treatmentPriceAcuOnly}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="電（鍼・温）"
        value={formatNumber(display.electric_treatment_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="施術報告書交付料"
        value={formatNumber(display.treatment_report_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
    </AppCard>
  );

  // --- A2 Old Law Right Column ---
  const a2OldLawRightColumn = <AppCard className="p-2">{distancePricesBlock}</AppCard>;

  // --- R2 Left Column ---
  const r2LeftColumn = (
    <AppCard className="p-2">
      {showNewLaw ? (
        <>
          <InfoRow
            label="マッサージ　１局所"
            value={formatNumber(display.massage_non_inclusion_price1)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
          <InfoRow
            label="マッサージ　２局所"
            value={formatNumber(display.massage_non_inclusion_price2)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
          <InfoRow
            label="マッサージ　３局所"
            value={formatNumber(display.massage_non_inclusion_price3)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
          <InfoRow
            label="マッサージ　４局所"
            value={formatNumber(display.massage_non_inclusion_price4)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
          <InfoRow
            label="マッサージ　５局所"
            value={formatNumber(display.massage_non_inclusion_price5)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
        </>
      ) : (
        <InfoRow
          label="マッサージ"
          value={formatNumber(display.massage_price)}
          labelWidth="w-1/2"
          classNameLabel="text-gray-800"
          classNameValue="text-right"
        />
      )}
      <InfoRow
        label="変形徒手矯正術"
        value={formatNumber(display.orthomorphia_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="温罨法"
        value={formatNumber(display.hot_treatment_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      <InfoRow
        label="電療"
        value={formatNumber(display.electric_treatment_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
      {showNewLaw && (
        <>
          {showNewLawJun2024 ? (
            <>
              <InfoRow
                label="4km以下"
                value={formatNumber(display.distance_price4)}
                labelWidth="w-1/2"
                classNameLabel="text-gray-800"
                classNameValue="text-right"
              />
              <InfoRow
                label="4.1km～16km"
                value={formatNumber(display.distance_price16)}
                labelWidth="w-1/2"
                classNameLabel="text-gray-800"
                classNameValue="text-right"
              />
            </>
          ) : (
            <InfoRow
              label="往療料"
              value={formatNumber(display.distance_price2)}
              labelWidth="w-1/2"
              classNameLabel="text-gray-800"
              classNameValue="text-right"
            />
          )}
          <InfoRow
            label="特別地域加算料"
            value={formatNumber(display.special_area_addition_price)}
            labelWidth="w-1/2"
            classNameLabel="text-gray-800"
            classNameValue="text-right"
          />
        </>
      )}
      <InfoRow
        label="施術報告書交付料"
        value={formatNumber(display.treatment_report_price)}
        labelWidth="w-1/2"
        classNameLabel="text-gray-800"
        classNameValue="text-right"
      />
    </AppCard>
  );

  // --- R2 Right Column ---
  const r2RightColumn = (
    <AppCard className="p-2">
      {showNewLaw ? (
        <>
          {([1, 2, 3, 4] as const).map(tier =>
            ([1, 2, 3, 4, 5] as const).map(region => {
              const key = `visit_treatment_price${tier}_${region}` as keyof typeof display;
              return (
                <InfoRow
                  key={key}
                  label={`訪問施術料${['⓵', '⓶', '⓷', '⓸'][tier - 1]}　${region}局所`}
                  value={formatNumber(display[key] as number | null)}
                  labelWidth="w-1/2"
                  classNameLabel="text-gray-800"
                  classNameValue="text-right"
                />
              );
            })
          )}
        </>
      ) : (
        distancePricesBlock
      )}
    </AppCard>
  );

  const a2LeftColumn = showNewLaw ? a2NewLawLeftColumn : a2OldLawLeftColumn;
  const a2RightColumn = showNewLaw ? a2NewLawRightColumn : a2OldLawRightColumn;

  return (
    <div className="relative">
      {isFetching && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md bg-white/60"
          aria-busy="true"
        >
          <AppSpin size="large" />
        </div>
      )}

      <AppSpace orientation="vertical" size="large" className="w-full">
        <CollapsibleCard title="健康保険料金">
          <div className="flex items-center gap-4 px-4 sm:w-[75%] md:w-[70%] lg:w-[60%] xl:w-1/2 2xl:w-1/3">
            <AppTypography.Text className="text-gray-700 whitespace-nowrap">
              適用期間
            </AppTypography.Text>
            <AppTextField
              className="bg-gray-100 border rounded"
              value={formatAvailableDate(display.available_from)}
              disabled
            />
            <AppTypography.Text className="text-center">～</AppTypography.Text>
            <AppTextField
              className="bg-gray-100 border rounded"
              value={formatAvailableDate(display.available_to)}
              disabled
            />
          </div>

          <AppRow className="justify-center mt-4">
            <AppCol>
              <AppPagination
                current={page}
                total={isA2 ? a2Pages.length : (data?.data.pagination.total ?? 0)}
                pageSize={1}
                onChange={p => setPage(p)}
              />
            </AppCol>
          </AppRow>
        </CollapsibleCard>

        <AppRow gutter={[24, 24]}>
          <AppCol xs={24} lg={12}>
            {isA2 ? a2LeftColumn : r2LeftColumn}
          </AppCol>
          <AppCol xs={24} lg={12}>
            {isA2 ? a2RightColumn : r2RightColumn}
          </AppCol>
        </AppRow>
      </AppSpace>
    </div>
  );
};

export default MedicalExpensePricePage;
